import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { signInWithGoogle } from './firebase';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import NavBar from './NavBar';
import './Features.css';

function Features() {
  useEffect(() => {
    // First scroll to top immediately
    window.scrollTo(0, 0);

    // Handle scroll to anchor after page loads
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          // Get the element's position from the top of the document
          const elementPosition = element.offsetTop;
          // Scroll to that position with a small offset
          window.scrollTo({ top: elementPosition - 20, behavior: 'smooth' });
        }
      }, 300);
    }
  }, []);

  return (
    <div className="features-page">
      <NavBar />

      {/* Hero Section */}
      <section className="features-hero">
        <div className="features-hero-content">
          <h1>Features</h1>
          <p className="features-subtitle">Everything you need to build powerful AI agents</p>
        </div>
      </section>

      {/* Feature Sections */}
      <div className="features-content">

        {/* Simple Tool Creation */}
        <section id="simple-tool-creation" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🛠️ Simple Tool Creation</h2>
              <p>
                Creating tools for your agents is as simple as writing a Python function.
                No complex schemas, no boilerplate code, no wrestling with provider-specific formats.
              </p>
              <p>
                Orchestral automatically generates JSON schemas from your function signatures and docstrings.
                Type hints provide automatic validation, and default values just work.
              </p>
              <p>
                The entire zoo of different APIs and standards across LLM providers is handled
                transparently—you write the function once, and it works everywhere.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral import define_tool

@define_tool()
def divide_tool(a: float, b: float = 1.0):
    """Divide two numbers a and b
    Returns a / b
    """
    return a / b

agent = Agent(tools=[divide_tool])`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Easy Agent Setup */}
        <section id="easy-agent-setup" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🤖 Easy Agent Setup</h2>
              <p>
                Create intelligent agents in seconds with minimal configuration.
                No boilerplate required—just instantiate and run.
              </p>
              <p>
                Register tools, configure context modes, set token limits, and start orchestrating
                complex workflows with just a few lines of code.
              </p>
              <p>
                Agents automatically handle multi-turn conversations, tool execution loops,
                and context management out of the box.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent

agent = Agent()

response = agent.run('Hello world!')
print(response.text)
`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Streaming UI */}
        <section id="streaming-ui" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>⚡ Streaming UI</h2>
              <p>
                Every major provider handles streaming differently, with their own response formats,
                metadata structures, and quirks. Orchestral abstracts all of this complexity away.
              </p>
              <p>
                We expose only the text chunks you actually want to display, while keeping track
                of all the context, tool calls, and metadata under the hood. No parsing provider-specific
                response objects or handling edge cases.
              </p>
              <p>
                Just iterate over the stream and display the text. That's it. The streaming interface
                is unified across all providers, so your code works everywhere.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`streaming_response = agent.stream_text_message('Hello!')
for chunk in streaming_response:
    print(chunk, end='', flush=True)
`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Multi-Turn Execution */}
        <section id="multi-turn-execution" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🔄 Multi-Turn Execution</h2>
              <p>
                A tool call is deceptively simple. Under the hood it requires detecting the call,
                executing the proper tool, formatting and appending the result, and calling the LLM
                again. This is all handled automatically so you can focus on the interesting stuff.
              </p>
              <p>
                Agents can chain multiple tool calls, observe results, and decide on next actions
                without any manual orchestration of the request-response cycle.
              </p>
              <p>
                Built-in iteration limits and cost tracking keep everything under control.{' '}
                <Link to="/blog/function-calling" style={{ color: '#d7af00', textDecoration: 'none' }}>
                  Learn more about how function calling works →
                </Link>
              </p>
            </div>
            <div className="feature-code">
              <img src="/ui_multi-turn.png" alt="Multi-turn execution example showing agent chaining tool calls" style={{ width: '100%', borderRadius: '8px' }} />
            </div>
          </div>
        </section>

        {/* Provider Agnostic */}
        <section id="provider-agnostic" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🧩 Provider Agnostic</h2>
              <p>
                Support for all popular LLM providers including local inference.
                Switch between OpenAI, Anthropic, Google, xAI, and local models with a single line change.
              </p>
              <p>
                Each provider has different APIs, parameters, and quirks—Orchestral handles them all
                seamlessly with a unified interface.
              </p>
              <p>
                Your agent code stays the same regardless of which LLM you choose.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import *

# llm = Claude()
# llm = Gemini()
# llm = Groq()
# llm = Llama()
# llm = LMStudio()
llm = GPT(model='gpt-5')

agent = Agent(llm=llm)`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Smart Context */}
        <section id="smart-context" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>💡 Smart Context Management</h2>
              <p>
                Message history management with sensible defaults out of the box. Context compaction
                and caching work automatically to keep conversations efficient without sacrificing
                important information.
              </p>
              <p>
                We offer additional avenues to protect your context: When calling tools, the context
                can be unintentionally assaulted by extremely long results. We add Truncation and
                Summarization Hooks to guard against these issues.
              </p>
              <p>
                We believe good context engineering is achieved by filtering signal from noise at every step.
              </p>
              <p>
                Need custom context management? Orchestral provides a clean framework to implement
                your own strategies. Extend the base compactor class and define exactly how you want
                to handle message history, summarization, or pruning.
              </p>
              <p>
                Built-in caching support for providers that offer it, reducing latency and costs
                without any additional configuration.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem', marginBottom: '2rem'}}>
{`# Sensible defaults work out of the box
agent.context.compact()

# Or enable automatic compaction
agent.context.auto_compact = True

# Protect context with hooks
from orchestral.hooks import (
    TruncationHook,
    SummarizationHook
)

agent = Agent(hooks=[
    TruncationHook(max_length=5000),
    SummarizationHook()
])`}
              </SyntaxHighlighter>

              Custom context management:
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral.context import BaseCompactor

class CustomCompactor(BaseCompactor):
    def compact(self, messages):
        # Your custom logic here
        return compacted_messages

agent.context.compact(CustomCompactor)
`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Automatic Cost Tracking */}
        <section id="cost-tracking" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>💰 Cost Limits and Live Tracking</h2>
              <p>
                Built-in token usage and cost tracking across all providers with real-time monitoring
                in the beautiful web UI. Set cost limits to prevent runaway expenses during development.
              </p>
              <p>
                Every agent call returns detailed usage statistics including input tokens,
                output tokens, and estimated costs based on current provider pricing.
              </p>
              <p>
                Watch your costs and context usage in real-time as your agents work, with beautiful
                visualizations in the UI that keep you informed at every step.
              </p>
            </div>
            <div className="feature-code">
              Using the Python API:
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem', marginBottom: '2rem' }}>
{`agent.get_total_cost()`}
              </SyntaxHighlighter>
              In the UI:
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`> /cost
Total cost so far: $0.01810`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Beautiful Web UI */}
        <section id="web-ui" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🎨 Beautiful Web UI</h2>
              <p>
                Experience a modern, intuitive web interface alongside our powerful terminal UI.
                The web UI provides real-time cost and context usage tracking, beautiful visualizations
                of agent workflows, and an elegant way to interact with your agents.
              </p>
              <p>
                Monitor your agents' performance with live updates, inspect tool calls and results,
                and manage your AI workflows through a clean, responsive interface designed for
                both development and production use.
              </p>
            </div>
            <div className="feature-code">
              <img src="/ui_showcase_who_are_you.png" alt="Beautiful web UI showing agent interaction" style={{ width: '100%', borderRadius: '8px' }} />
            </div>
          </div>
        </section>

        {/* Subagents */}
        <section id="subagents" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🤖 Recursive Subagents</h2>
              <p>
                Subagents are treated as tools! We take complicated operations and turn them into
                simple high-level actions that agents can perform without thinking.
              </p>
              <p>
                Define flexible subagents that can be called recursively, enabling powerful hierarchical
                agent architectures. Parent agents can delegate complex tasks to specialized subagents,
                each with their own tools, context, and capabilities.
              </p>
              <p>
                This hierarchical approach allows you to build sophisticated multi-agent systems where
                each agent focuses on what it does best, while higher-level agents orchestrate the
                overall workflow.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent

# Create a specialized subagent
research_agent = Agent(
    name="Researcher",
    tools=[web_search, read_paper]
)

# Use it as a tool for the main agent
main_agent = Agent(
    subagents=[research_agent]
)

# The main agent can now delegate research tasks
response = main_agent.run(
    "Research recent advances in quantum computing"
)`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Easy Installation */}
        <section id="easy-install" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>📦 Easy Installation</h2>
              <p>
                Get started in seconds with a simple pip install. No complex build steps,
                no dependency hell, no configuration nightmares.
              </p>
              <p>
                Just set your API keys and you're ready to build intelligent agents.
                Orchestral handles all the complexity behind a clean, simple interface.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '1rem' }}>
{`pip install orchestral-ai`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* LaTeX Integration */}
        <section id="latex-integration" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>📝 LaTeX Integration</h2>
              <p>
                Seamlessly include beautifully formatted agent conversations in your research papers
                and documentation. Perfect for researchers who want to showcase agent interactions
                in publications.
              </p>
              <p>
                Export conversations with proper formatting, syntax highlighting for code blocks,
                and professional typesetting that integrates perfectly with your LaTeX workflow.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`# Export conversation to LaTeX
agent.export_conversation_latex("output.tex")

# Include in your paper
# \\input{output.tex}`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Local Models */}
        <section id="local-models" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>💻 Local Model Support</h2>
              <p>
                Automatically detect and serve models installed locally with Ollama. Run your agents
                completely offline with local models while maintaining the same simple API.
              </p>
              <p>
                Orchestral seamlessly integrates with your local model infrastructure, giving you
                full control over your AI stack without vendor lock-in.
              </p>
              <p className="feature-note">
                * Some small models lacking tool support will not be supported. Orchestral is designed
                around tools, and we prioritize supporting models that integrate with them.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import Ollama

llm = Ollama(model='llama3')
agent = Agent(llm=llm)`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Hooks System */}
        <section id="hooks-system" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🔗 Hooks System</h2>
              <p>
                Elegantly define pre/post-call hooks which wrap your tool calls. These hooks are
                versatile and useful for security, memory management, context protection, and more.
              </p>
              <p>
                Hooks let you intercept and modify agent behavior at critical points, enabling
                powerful patterns like automatic approval workflows, cost limiting, result caching,
                and custom logging.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral.hooks import Hook

class LoggingHook(Hook):
    def pre_call(self, tool_name, args):
        print(f"Calling {tool_name} with {args}")
        return args

    def post_call(self, result):
        print(f"Result: {result}")
        return result

agent = Agent(hooks=[LoggingHook()])`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Safety System */}
        <section id="safety-system" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>🛡️ Safety System</h2>
              <p>
                LLMs can sometimes be unreliable or unpredictable, and may sometimes choose to execute
                code or take actions you don't want. We support a flexible and customizable system for
                filtering and approving tool calls.
              </p>
              <p>
                <strong>UserApprovalHooks:</strong> You are prompted in the UI to manually approve all tools,
                giving you complete control over what your agents can do.
              </p>
              <p>
                <strong>SafeguardHook:</strong> Use an "LLM as Judge" implemented with a "Safeguard Model"
                following a carefully crafted security policy. The model is served by Groq with incredible
                speed. The SafeguardAgent silently checks all code and filters into three categories:
                Definitely Safe, Definitely Harmful, or Borderline (requiring user approval).
              </p>
              <p>
                <strong>DangerousCommandHook:</strong> Use heuristics to filter out harmful commands with
                pattern matching and blocklists.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral.hooks import (
    UserApprovalHook,
    SafeguardHook,
    DangerousCommandHook
)

# Choose your safety level
agent = Agent(hooks=[
    SafeguardHook(),  # AI-powered safety
    # UserApprovalHook(),  # Manual approval
    # DangerousCommandHook(),  # Heuristic filtering
])`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>

        {/* Terminal UI */}
        <section id="terminal-ui" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>⚡ Terminal UI</h2>
              <p>
                A powerful terminal interface with real-time streaming, syntax highlighting,
                and rich formatting. Perfect for developers who prefer working in the terminal.
              </p>
              <p>
                Watch your agents think and act with beautiful, responsive displays that bring
                clarity to complex agent workflows.
              </p>
            </div>
            <div className="feature-code">
              <img src="/ui_showcase_tools.png" alt="Terminal UI showing tool execution" style={{ width: '100%', borderRadius: '8px' }} />
            </div>
          </div>
        </section>

        {/* Lightweight & Modular */}
        <section id="lightweight-modular" className="feature-section">
          <div className="feature-container">
            <div className="feature-text">
              <h2>📦 Lightweight & Modular</h2>
              <p>
                Clean, minimal codebase with zero unnecessary dependencies.
                Use only what you need for maximum flexibility and performance.
              </p>
              <p>
                The entire framework is designed around composable components.
                Extend and customize without fighting the framework.
              </p>
              <p>
                No bloated abstractions—just clean Python code that does exactly what you expect.
              </p>
            </div>
            <div className="feature-code">
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral.llm.base import LLM, Response
from orchestral.context import Context
from orchestral.context import Message
from orchestral.llm import GPT

class Agent:
    ...`}
              </SyntaxHighlighter>
            </div>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <section className="features-cta">
        <div className="features-cta-content">
          <h2>Ready to Build?</h2>
          <p>Start orchestrating AI agents with all these powerful features</p>
          <button className="features-cta-button" onClick={signInWithGoogle}>
            Get Started
          </button>
        </div>
      </section>
    </div>
  );
}

export default Features;
